import React from 'react';

import {
  DateFilterValue,
  TypeFilterValue,
  RoomsFilterValue,
  PriceFilterValue,
  FurnitureFilterValue,
  BillsFilterValue,
  SuitableForFilterValue,
  GenderFilterValue,
  RulesFilterValue,
  RegistrationFilterValue,
  RecentlyAddedFilterValue,
  ContractTypeFilterValue,
  SearchAreaFilterValue,
  PropertySizeFilterValue,
  FacilitiesFilterValue,
  AmenitiesFilterValue,
  RadiusFilterValue,
  RadiusValue,
  AvailabilityFilterValue,
} from 'ha/types/SearchFilters';

import { PriceParams } from 'ha/components/Filters/PriceFilter';
import { FiltersCount } from 'ha/pages/Search/types';
import { DEFAULT_CURRENCY } from 'ha/constants/Currencies';

export interface LocalState {
  dates: DateFilterValue;
  types: TypeFilterValue;
  furniture: FurnitureFilterValue;
  bills: BillsFilterValue;
  rooms: RoomsFilterValue;
  rules: RulesFilterValue;
  price: PriceFilterValue;
  currency: string;
  registration: RegistrationFilterValue;
  searchArea: SearchAreaFilterValue;
  radius: RadiusFilterValue;
  place: string;
  recentlyAdded: RecentlyAddedFilterValue;
  gender: GenderFilterValue;
  suitableFor: SuitableForFilterValue;
  contractType: ContractTypeFilterValue;
  propertySize?: PropertySizeFilterValue;
  facilities: FacilitiesFilterValue;
  amenities: AmenitiesFilterValue;
}

export interface SearchFiltersContextValue {
  localState: LocalState;
  filtersCount: FiltersCount;
  priceParams: PriceParams;
  onChangeDates: (_: DateFilterValue) => void;
  onChangeAvailability: (_: AvailabilityFilterValue) => void;
  onChangeTypes: (_: TypeFilterValue) => void;
  onChangeRooms: (_: RoomsFilterValue) => void;
  onChangePrice: (_: PriceFilterValue) => void;
  onChangeCurrency: (_: string) => void;
  onChangeFurniture: (_: FurnitureFilterValue) => void;
  onChangeBills: (_: BillsFilterValue) => void;
  onChangeSuitableFor: (_: SuitableForFilterValue) => void;
  onChangeGender: (_: GenderFilterValue) => void;
  onChangeRules: (_: RulesFilterValue) => void;
  onChangeRegistration: (_: RegistrationFilterValue) => void;
  onChangeSearchAreaFilter: (_: SearchAreaFilterValue) => void;
  onChangeRadius: (_: RadiusFilterValue) => void;
  onChangePlace: (_: string) => void;
  onChangeRecentlyAdded: (_: RecentlyAddedFilterValue) => void;
  onChangeContractType: (_: ContractTypeFilterValue) => void;
  onChangePropertySize: (_: PropertySizeFilterValue) => void;
  onChangeFacilities: (_: FacilitiesFilterValue) => void;
  onChangeAmenities: (_: AmenitiesFilterValue) => void;
  clearFilters: (filtersToKeep?: Array<keyof LocalState>) => void;
  isFilterEmpty: (filterName: keyof LocalState) => boolean;
  applyFilters: () => void;
}

export const EmptyState: LocalState = {
  dates: {
    startDate: null,
    endDate: null,
  },
  types: [],
  furniture: [],
  bills: [],
  rooms: {
    bedroomCount: [],
  },
  rules: [],
  price: {
    min: null,
    max: null,
  },
  currency: DEFAULT_CURRENCY,
  registration: [],
  searchArea: [],
  recentlyAdded: null,
  gender: [],
  suitableFor: [],
  contractType: [],
  radius: RadiusValue.DEFAULT,
  place: '',
  propertySize: {
    sizeMin: undefined,
    sizeMax: undefined,
  },
  facilities: [],
  amenities: [],
};

export const SearchFiltersContext =
  React.createContext<SearchFiltersContextValue>({
    localState: EmptyState,
    filtersCount: {
      furniture: {},
      suitableFor: {},
      gender: {},
      registration: {},
      bills: {},
      propertyType: {},
      contractType: {},
      propertySize: {},
      facilities: {},
      amenities: {},
      rooms: {},
    },
    priceParams: {
      priceMin: 0,
      priceMax: 0,
      priceDistribution: [],
    },
    /* eslint-disable @typescript-eslint/no-unused-vars */
    onChangeDates: (_: DateFilterValue) => {},
    onChangeTypes: (_: TypeFilterValue) => {},
    onChangeRooms: (_: RoomsFilterValue) => {},
    onChangePrice: (_: PriceFilterValue) => {},
    onChangeCurrency: (_: string) => {},
    onChangeFurniture: (_: FurnitureFilterValue) => {},
    onChangeBills: (_: BillsFilterValue) => {},
    onChangeSuitableFor: (_: SuitableForFilterValue) => {},
    onChangeGender: (_: GenderFilterValue) => {},
    onChangeRules: (_: RulesFilterValue) => {},
    onChangeRegistration: (_: RegistrationFilterValue) => {},
    onChangeSearchAreaFilter: (_: SearchAreaFilterValue) => {},
    onChangeRadius: (_: RadiusFilterValue) => {},
    onChangePlace: (_: string) => {},
    onChangeRecentlyAdded: (_: RecentlyAddedFilterValue) => {},
    onChangeContractType: (_: ContractTypeFilterValue) => {},
    onChangePropertySize: (_: PropertySizeFilterValue) => {},
    onChangeFacilities: (_: FacilitiesFilterValue) => {},
    onChangeAmenities: (_: AmenitiesFilterValue) => {},
    clearFilters: () => {},
    isFilterEmpty: () => true,
    applyFilters: () => {},
    onChangeAvailability: (_: AvailabilityFilterValue) => {},
    /* eslint-enable @typescript-eslint/no-unused-vars */
  });

SearchFiltersContext.displayName = 'SearchFiltersContext';
